import PropTypes from 'prop-types';
import React, { lazy } from 'react';
import styled from '@emotion/styled';

import SuspenseHelper from '~/components/SuspenseHelper';

import theme from '~/theme';

import { transition } from '~/styles/transition';

const Icons = {
    'arrow': lazy(() => import('~/icons/Arrow')),
    'plus': lazy(() => import('~/icons/Plus')),
    'minus': lazy(() => import('~/icons/Minus')),
    'location': lazy(() => import('~/icons/Location')),
    'rewards': lazy(() => import('~/icons/Rewards')),
    'user': lazy(() => import('~/icons/User')),
    'coin': lazy(() => import('~/icons/Coin')),
    'search': lazy(() => import('~/icons/Search')),
    'facebook': lazy(() => import('~/icons/Facebook')),
    'instagram': lazy(() => import('~/icons/Instagram')),
    'x': lazy(() => import('~/icons/X')),
    'shopping-bag': lazy(() => import('~/icons/ShoppingBag')),
    'chevron-down': lazy(() => import('~/icons/ChevronDown')),
    'chevron-right': lazy(() => import('~/icons/ChevronRight')),
    'circle-arrow': lazy(() => import('~/icons/CircleArrow')),
    'breadcrumb-chevron': lazy(() => import('~/icons/BreadcrumbChevron')),
    'newspaper': lazy(() => import('~/icons/Newspaper')),
    'washes': lazy(() => import('~/icons/Washes')),
    'washes-plus': lazy(() => import('~/icons/WashesPlus')),
    'wallet': lazy(() => import('~/icons/Wallet')),
    'phone': lazy(() => import('~/icons/Phone')),
    'trophy': lazy(() => import('~/icons/Trophy')),
    'hamburger': lazy(() => import('~/icons/Hamburger')),
    'close': lazy(() => import('~/icons/Close')),
    'garage': lazy(() => import('~/icons/Garage')),
    'bell-coin': lazy(() => import('~/icons/BellCoin')),
    'lock': lazy(() => import('~/icons/Lock')),
    'checkmark': lazy(() => import('~/icons/Checkmark')),
    'pencil': lazy(() => import('~/icons/Pencil')),
    'lightning': lazy(() => import('~/icons/Lightning')),
    'credit-card': lazy(() => import('~/icons/CreditCard')),
    'gear': lazy(() => import('~/icons/Gear')),
    'car-wash': lazy(() => import('~/icons/CarWash')),
    'vehicle': lazy(() => import('~/icons/Vehicle')),
    'star': lazy(() => import('~/icons/Star')),
    'solid-wallet': lazy(() => import('~/icons/SolidWallet')),
    'solid-garage': lazy(() => import('~/icons/SolidGarage')),
    'car': lazy(() => import('~/icons/Car')),
    'gift': lazy(() => import('~/icons/Gift'))
};

const Wrapper = styled.i`
    display: inline-block;
    width: ${({ iconSizeMobile, autoWidth }) =>
        autoWidth ? 'auto' : `${iconSizeMobile}px`};
    height: ${({ iconSizeMobile }) => `${iconSizeMobile}px`};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        width: ${({ iconSizeTablet, autoWidth }) =>
            autoWidth ? 'auto' : `${iconSizeTablet}px`};
        height: ${({ iconSizeTablet }) => `${iconSizeTablet}px`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        width: ${({ iconSize, autoWidth }) =>
            autoWidth ? 'auto' : `${iconSize}px`};
        height: ${({ iconSize }) => `${iconSize}px`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        width: ${({ iconSizeLarge, autoWidth }) =>
            autoWidth ? 'auto' : `${iconSizeLarge}px`};
        height: ${({ iconSizeLarge }) => `${iconSizeLarge}px`};
    }

    svg {
        vertical-align: top;
        height: 100%;
        width: 100%;

        * {
            ${transition({}, 'color')};
            color: ${({ iconColor }) => iconColor};
        }
    }
`;

const Loader = styled.span`
    display: block;
    font-size: ${({ size }) => `${size}px`};
    height: ${({ size }) => `${size}px`};
    width: ${({ size }) => `${size}px`};
`;

const Icon = ({
    icon,
    size = 18,
    autoWidth = false,
    sizeTablet,
    sizeMobile,
    sizeLarge,
    color = 'blue',
    className = ''
}) => {
    const IconPath = Icons[icon];

    return IconPath ? (
        <Wrapper
            aria-hidden="true"
            iconSize={size}
            iconSizeMobile={sizeMobile ?? sizeTablet ?? size}
            iconSizeTablet={sizeTablet ?? size}
            iconSizeLarge={sizeLarge ?? sizeTablet ?? size}
            iconColor={theme.iconColors[color]}
            {...{ className, autoWidth }}
        >
            <SuspenseHelper fallback={<Loader />}>
                <IconPath />
            </SuspenseHelper>
        </Wrapper>
    ) : null;
};

Icon.propTypes = {
    icon: PropTypes.string.isRequired,
    size: PropTypes.number,
    autoWidth: PropTypes.bool,
    sizeTablet: PropTypes.number,
    sizeMobile: PropTypes.number,
    sizeLarge: PropTypes.number,
    color: PropTypes.oneOf(Object.keys(theme.iconColors)),
    className: PropTypes.string
};

export default Icon;
